import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
  Send as SendIcon,
  LibraryBooks as TemplateIcon,
  PictureAsPdf as PdfIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PlanificationService from '../../services/planification.service';
import PlanificationTypeService from '../../services/planificationType.service';
import CursoService from '../../services/curso.service';
import http from '../../http-common';

const statusColor = (status) => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'sent':
      return 'info';
    case 'in_review':
      return 'warning';
    case 'needs_changes':
      return 'error';
    case 'approved':
      return 'success';
    default:
      return 'default';
  }
};

const PlanificationList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ planification_type_id: '', status: '', curso_id: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logoUrl, setLogoUrl] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState({});

  useEffect(() => {
    fetchTypes();
    fetchCursos();
    fetchLogo();
  }, []);

  const fetchCursos = async () => {
    try {
      const res = await CursoService.getAll();
      setCursos(res.data?.data || []);
    } catch (e) {
      console.error('Error loading cursos:', e);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await http.get('/settings');
      if (response.data.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [filters.planification_type_id, filters.status, filters.curso_id]);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchTypes = async () => {
    try {
      const res = await PlanificationTypeService.getAll();
      setTypes(res.data?.data || []);
    } catch (e) {
      console.error('Error loading planification types:', e);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.planification_type_id) params.planification_type_id = filters.planification_type_id;
      if (filters.status) params.status = filters.status;
      if (filters.curso_id) params.curso_id = filters.curso_id;
      const res = await PlanificationService.getAll(params);
      setItems(res.data?.data || []);
    } catch (e) {
      console.error('Error loading planifications:', e);
      showSnackbar(t('planifications.errorLoadingPlanifications'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter(p => (p.name || '').toLowerCase().includes(q) || (p.type?.name || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  const planificationTree = useMemo(() => {
    const byId = new Map();
    filtered.forEach(p => byId.set(p.id, { ...p, children: [] }));
    const roots = [];
    byId.forEach(node => {
      const parentId = node.parent_id ?? node.parent?.id;
      if (parentId && byId.has(parentId)) {
        byId.get(parentId).children.push(node);
      } else {
        roots.push(node);
      }
    });
    const sortRec = (nodes) => {
      nodes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      nodes.forEach(n => sortRec(n.children));
    };
    sortRec(roots);
    return roots;
  }, [filtered]);

  useEffect(() => {
    const idsWithChildren = new Set();
    const collectParentIds = (nodes) => {
      nodes.forEach((node) => {
        if ((node.children || []).length > 0) {
          idsWithChildren.add(node.id);
          collectParentIds(node.children);
        }
      });
    };
    collectParentIds(planificationTree);
    setExpandedTypes((prev) => {
      const next = { ...prev };
      idsWithChildren.forEach((id) => { next[id] = true; });
      return next;
    });
  }, [planificationTree]);

  const togglePlanificationExpand = (id) => {
    setExpandedTypes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSend = async (plan) => {
    try {
      await PlanificationService.send(plan.id);
      showSnackbar(t('planifications.planificationSent'));
      fetchItems();
    } catch (e) {
      console.error('Error sending planification:', e);
      showSnackbar(e.response?.data?.message || 'Error sending planification', 'error');
    }
  };

  const handleDownloadPdf = async (plan) => {
    try {
      const response = await PlanificationService.generatePdf(plan.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `planification_${plan.id}_${plan.name || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar(t('planifications.pdfDownloadedSuccess'));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showSnackbar(error.response?.data?.message || 'Error downloading PDF', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await PlanificationService.delete(deleteDialog.item.id);
      showSnackbar(t('planifications.planificationDeleted'));
      fetchItems();
    } catch (e) {
      console.error('Error deleting planification:', e);
      showSnackbar(t('planifications.errorDeletingPlanification'), 'error');
    } finally {
      setDeleteDialog({ open: false, item: null });
    }
  };

  const formatRange = (p) => {
    const s = p.start_date ? new Date(p.start_date).toLocaleDateString() : '';
    const e = p.end_date ? new Date(p.end_date).toLocaleDateString() : '';
    if (!s && !e) return '—';
    if (s && !e) return s;
    if (!s && e) return e;
    return `${s} - ${e}`;
  };

  const renderPlanificationNode = (plan, level = 0) => {
    const children = plan.children || [];
    const hasChildren = children.length > 0;
    const isOpen = !!expandedTypes[plan.id];

    return (
      <Box key={plan.id}>
        <Box sx={{ pl: level * 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: level > 0 ? 'action.hover' : undefined,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
              {hasChildren ? (
                <IconButton size="small" onClick={() => togglePlanificationExpand(plan.id)}>
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} />
              )}
              <Typography variant="body1" sx={{ fontWeight: level === 0 ? 600 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {plan.name}
              </Typography>
              <Chip label={plan.type?.name ?? '—'} size="small" variant="outlined" />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                {formatRange(plan)}
              </Typography>
              <Chip label={({ draft: t('planifications.statusDraft'), sent: t('planifications.statusSent'), in_review: t('planifications.statusInReview'), needs_changes: t('planifications.statusNeedsChanges'), approved: t('planifications.statusApproved') }[plan.status] || plan.status)} color={statusColor(plan.status)} size="small" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasPermission('planifications.create') && (
                <IconButton
                  size="small"
                  color="primary"
                  title={t('planifications.addChild')}
                  onClick={() => navigate(`/planifications/new?parent_id=${plan.id}`)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
              {hasPermission('planifications.edit') && (
                <IconButton size="small" color="primary" title={t('common.edit')} onClick={() => navigate(`/planifications/${plan.id}/edit`)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {hasPermission('planifications.send') && (plan.status === 'draft' || plan.status === 'needs_changes') && (
                <IconButton size="small" color="info" title={t('planifications.send')} onClick={() => handleSend(plan)}>
                  <SendIcon fontSize="small" />
                </IconButton>
              )}
              {plan.status === 'approved' && (
                <IconButton size="small" color="primary" title={t('planifications.downloadPdf')} onClick={() => handleDownloadPdf(plan)}>
                  <PdfIcon fontSize="small" />
                </IconButton>
              )}
              {hasPermission('planifications.delete') && (
                <IconButton size="small" color="error" title={t('common.delete')} onClick={() => setDeleteDialog({ open: true, item: plan })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box>
              {children.map((child) => renderPlanificationNode(child, level + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TemplateIcon />
              <Typography variant="h5">{t('planifications.title')}</Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Company Logo"
                  sx={{
                    height: 40,
                    width: 'auto',
                    maxWidth: 120,
                    objectFit: 'contain',
                    mr: 1
                  }}
                />
              )}
              <Button variant="outlined" onClick={() => navigate('/planifications/types')}>{t('planifications.typesButton')}</Button>
              <Button variant="outlined" startIcon={<TrashIcon />} onClick={() => navigate('/planifications/deleted')}>
                {t('planifications.deleted')}
              </Button>
              {hasPermission('planifications.create') && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/planifications/new')}>
                  {t('planifications.new')}
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              placeholder={t('planifications.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 320 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('common.type')}</InputLabel>
              <Select
                label={t('common.type')}
                value={filters.planification_type_id}
                onChange={(e) => setFilters(prev => ({ ...prev, planification_type_id: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {types.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                label={t('common.status')}
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="draft">{t('planifications.statusDraft')}</MenuItem>
                <MenuItem value="sent">{t('planifications.statusSent')}</MenuItem>
                <MenuItem value="in_review">{t('planifications.statusInReview')}</MenuItem>
                <MenuItem value="needs_changes">{t('planifications.statusNeedsChanges')}</MenuItem>
                <MenuItem value="approved">{t('planifications.statusApproved')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('planifications.curso')}</InputLabel>
              <Select
                label={t('planifications.curso')}
                value={filters.curso_id}
                onChange={(e) => setFilters(prev => ({ ...prev, curso_id: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {cursos.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : planificationTree.length === 0 ? (
            <Box py={3}>
              <Typography color="text.secondary">{t('planifications.emptyList')}</Typography>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {planificationTree.map((root) => renderPlanificationNode(root, 0))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>{t('planifications.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('planifications.deleteConfirm', { name: deleteDialog.item?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanificationList;
